
import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { Rule, ClassSet, Factor, ScoreClass } from "@/graphql/API";
import Scoring from "@/store/modules/Scoring";
import DateEditor from "@/components/viewpoints/viewpointMapping/editors/DateEditor.vue";
import ScoreClassCreator from "@/components/scoring/scoreClass/ScoreClassCreator.vue";
import VpDialog from "@/components/ui/VpDialog.vue";

const scoringModule = getModule(Scoring);

@Component({
    components: {
        DateEditor,
        ScoreClassCreator,
        VpDialog,
    },
})
export default class DateRule extends Vue {
    @Prop()
    factor!: Factor;

    @Prop()
    scoreRule!: Rule;

    @Prop()
    classRule!: Rule;

    @Prop()
    cluster!: ClassSet;

    @Prop()
    scoreRuleId!: number | null;

    @Prop({ default: null, type: Number })
    viewpointId!: number;

    @Prop({ default: false, type: Boolean })
    manualScore!: boolean;

    @Prop({ default: true, type: Boolean })
    factorsSynced!: boolean;

    @Prop({ default: false, type: Boolean })
    saving!: boolean;

    @Prop({ default: true, type: Boolean })
    editable!: boolean;

    private editModal = false;
    private selectedScoreClass: ScoreClass | null = null;
    private classIndex: number | null = null;

    private internalItems: {
        value: number | null;
        score: number;
        class_id: number | null;
    }[] = [];
    private minRangeScore = 0;
    private minRangeClass: null | number = null;
    private loading = false;

    get ruleIds(): string {
        return `${this.scoreRule ? this.scoreRule.id : "null"}-${
            this.classRule ? this.classRule.id : "null"
        }-${this.cluster ? this.cluster.id : "null"}`;
    }

    get factorRule(): string {
        return `${this.factor ? this.factor.id.toString() : "null"}-${
            this.viewpointId ? this.viewpointId : "null"
        }`;
    }

    get ruleChanged(): boolean {
        return this.rangesChanged || this.classesChanged;
    }

    get currentScores(): number[] {
        if (this.internalItems) {
            return this.internalItems.map((item) => item.score);
        }
        return [];
    }

    get duplicateScores(): boolean {
        if (this.manualScore) {
            return (
                new Set(this.currentScores).size !== this.currentScores.length
            );
        } else {
            return false;
        }
    }

    get classBelowMin(): number | null {
        if (this.classSet && this.classSet[0]) {
            return this.classSet[0];
        } else {
            return null;
        }
    }

    get internalClassList(): { [index: number]: number | null } {
        if (this.minRangeClass != null) {
            return this.internalItems.reduce(
                (obj, item, index) => {
                    if (item.class_id) {
                        return {
                            ...obj,
                            [index + 1]: item.class_id,
                        };
                    } else {
                        return obj;
                    }
                },
                { 0: this.minRangeClass }
            );
        } else {
            return this.internalItems.reduce((obj, item, index) => {
                if (item.class_id) {
                    return {
                        ...obj,
                        [index + 1]: item.class_id,
                    };
                } else {
                    return obj;
                }
            }, {});
        }
    }

    get classSet(): { [index: number]: number } {
        if (this.cluster) {
            return this.cluster.mappings.reduce((obj, item) => {
                return {
                    ...obj,
                    [item.index]: item.class_id,
                };
            }, {});
        } else {
            return {};
        }
    }

    get btnLabel(): string {
        if (this.saving) {
            return "Saving...";
        } else if (this.ruleChanged) {
            return "Save";
        } else {
            return this.factorsSynced ? "Save" : "Sync Factors";
        }
    }

    get btnMsg(): string {
        if (this.loading) {
            return "Loading...";
        } else if (!(this.rangesChanged || this.classesChanged)) {
            return "Make a change to save";
        } else if (this.duplicateScores) {
            return "Cannot have duplicate scores";
        } else if (this.containsNull) {
            return "Please choose dates";
        } else {
            return "Click to save";
        }
    }

    get scoreItems(): {
        value: number;
        score: number;
        class_id: number | null;
    }[] {
        if (
            this.manualScore &&
            this.classRule &&
            this.classRule.index_uses_score
        ) {
            return this.classRule.ranges
                .sort((a, b) => {
                    return a.at_least - b.at_least;
                })
                .map((range) => {
                    return {
                        value: range.at_least,
                        score: range.at_least,
                        class_id: this.classSet[range.score],
                    };
                });
        } else if (
            this.scoreRule &&
            this.scoreRule.is_range &&
            this.scoreRule.ranges.length > 0
        ) {
            return this.scoreRule.ranges
                .sort((a, b) => {
                    return a.at_least - b.at_least;
                })
                .map((range, index) => {
                    return {
                        value: range.at_least,
                        score: range.score,
                        class_id: this.classSet[index + 1]
                            ? this.classSet[index + 1]
                            : null,
                    };
                });
        } else {
            return [];
        }
    }

    get classList(): { [index: number]: number } {
        if (this.cluster?.mappings) {
            return [...this.cluster.mappings].reduce((obj, item) => {
                return {
                    ...obj,
                    [item.index]: item.class_id,
                };
            }, {});
        } else {
            return {};
        }
    }

    /* Properties for detecting changes */
    get minRangeChange(): boolean {
        return !(this.minRangeScore == this.scoreRule.min_range_score);
    }

    get rangesChanged(): boolean {
        if (this.internalItems.length == this.scoreItems.length) {
            return !this.internalItems.every((item, index) => {
                return (
                    item.value == this.scoreItems[index].value &&
                    item.score == this.scoreItems[index].score
                );
            });
        } else {
            return true;
        }
    }

    get containsNull(): boolean {
        if (!this.manualScore) {
            return (
                this.internalItems.filter((item) => item.value == null).length >
                0
            );
        } else {
            return false;
        }
    }

    get classesChanged(): boolean {
        return (
            JSON.stringify(this.internalClassList) !=
            JSON.stringify(this.classList)
        );
    }

    get unsavedChanges(): boolean {
        return this.ruleChanged && !this.loading && !this.saving;
    }

    private async toggleClass(e: {
        class: number;
        range: number;
    }): Promise<void> {
        if (e && e.class && scoringModule.scoreClasses[e.class]) {
            this.selectedScoreClass = scoringModule.scoreClasses[e.class];
            this.classIndex = e.range;
            this.editModal = true;
        }
    }

    private async setNewClass(newClass: ScoreClass) {
        this.editModal = false;
        if (this.classIndex != null) {
            this.editModal = false;
            this.selectedScoreClass = null;
            if (this.classIndex > -1) {
                Vue.set(
                    this.internalItems[this.classIndex],
                    "class_id",
                    newClass.id
                );
            } else {
                this.minRangeClass = newClass.id;
            }
            this.classIndex = null;
            await this.saveRanges();
        }
    }

    private async saveRanges(): Promise<void> {
        this.$emit("save-ranges", {
            classes: this.internalClassList,
            ranges: this.manualScore
                ? this.internalItems
                : this.internalItems.filter((item) => item.value != null),
            min: this.minRangeScore,
        });
    }

    mounted(): void {
        this.onclassBelowMinChange();
        this.internalItems = this.scoreItems.map((a) => Object.assign({}, a));
        if (this.manualScore) {
            this.internalItems.sort((a, b) => {
                return a.score - b.score;
            });
        }
        this.minRangeScore = this.scoreRule
            ? this.scoreRule.min_range_score
            : 0;
    }

    @Watch("ruleIds")
    onRuleChange(): void {
        this.internalItems = this.scoreItems.map((a) => Object.assign({}, a));
        if (this.manualScore) {
            this.internalItems.sort((a, b) => {
                return a.score - b.score;
            });
        }
        this.minRangeScore = this.scoreRule
            ? this.scoreRule.min_range_score
            : 0;
    }

    @Watch("classBelowMin")
    onclassBelowMinChange(): void {
        this.minRangeClass = this.classBelowMin;
    }

    @Watch("unsavedChanges")
    onUnsavedChangesChanged(): void {
        scoringModule.setUnsavedRuleChanges(this.unsavedChanges);
    }
}
